<template>
  <div :class="{ show: isOpen }" class="modal-overlay">
    <transition name="modal">
      <div v-if="isOpen" class="modal-popup modal-popup--device">
        <div @click="close" class="close-modal">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            stroke-width="4"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </div>
        <img
          v-if="isTrialUser && !currentDevice.isTrialDevice"
          class="pro-ribbon--device-popup"
          src="@/assets/images/polygon.png"
          alt=""
        />
        <img
          v-if="isTrialUser && !currentDevice.isTrialDevice"
          class="pro-ribbon-icon--device-popup"
          src="@/assets/images/crown.svg"
          alt="pro"
        />
        <div class="popup-body">
          <div class="tab-content">
            <div class="popup-body-left">
              <img
                class="max-w-[250px]"
                :src="getImageUrl"
                alt="device image"
              />
            </div>
            <div class="popup-body-right">
              <div class="tabs">
                <div
                  v-for="tab in tabs"
                  :key="tab.id"
                  @click="changeTab(tab.id)"
                  class="tab"
                  :class="[{ active: activeTab === tab.id }]"
                >
                  <span>{{ $t(tab.name) }}</span>
                </div>
              </div>

              <div
                v-if="activeTab === 0"
                class="w-full flex-1 flex justify-center flex-col gap-[14px]"
              >
                <div class="row">
                  <div class="col !pl-0">
                    <div class="title">
                      {{ $t("popup.specs.title_details") }}
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col bg-greyscale-100 dark:bg-[#282C34]">
                    <span>{{ $t("device.spec.brand") }}</span>
                    <p>{{ currentDevice.deviceBrand }}</p>
                  </div>
                  <div class="col">
                    <span>{{ $t("device.spec.name") }}</span>
                    <p>{{ currentDevice.deviceName }}</p>
                  </div>
                  <div class="col bg-greyscale-100 dark:bg-[#282C34]">
                    <span>{{ $t("device.spec.model") }}</span>
                    <p>{{ currentDevice.deviceModel }}</p>
                  </div>
                  <div class="col">
                    <span>{{ $t("device.spec.version") }}</span>
                    <p>{{ currentDevice.osVersion }}</p>
                  </div>
                  <div class="col bg-greyscale-100 dark:bg-[#282C34]">
                    <span>{{ $t("device.spec.os") }}</span>
                    <p>{{ currentDevice.deviceOs }}</p>
                  </div>
                  <div class="col">
                    <span>{{ $t("device.spec.udid") }}</span>
                    <p>{{ currentDevice.deviceToken }}</p>
                  </div>
                  <div class="col bg-greyscale-100 dark:bg-[#282C34]">
                    <span>{{ $t("device.spec.resolution") }}</span>
                    <p>{{ currentDevice.resolution }}</p>
                  </div>
                  <div class="col">
                    <span>{{ $t("device.spec.size") }}</span>
                    <p>{{ currentDevice.screenSize }}</p>
                  </div>
                  <div class="col bg-greyscale-100 dark:bg-[#282C34]">
                    <span>{{ $t("device.spec.release") }}</span>
                    <p>
                      {{ currentDevice.releaseDate | moment("MMMM YYYY") }}
                    </p>
                  </div>
                  <div class="col">
                    <span>{{ $t("device.spec.ram") }}</span>
                    <p>
                      {{ currentDevice.ram }}
                    </p>
                  </div>
                  <div class="col bg-greyscale-100 dark:bg-[#282C34]">
                    <span>{{ $t("device.spec.capacity") }}</span>
                    <p>
                      {{ currentDevice.capacity }}
                    </p>
                  </div>
                  <div class="col">
                    <span>{{ $t("device.spec.purchased") }}</span>
                    <p>
                      {{ currentDevice.purchased }}
                    </p>
                  </div>
                </div>
              </div>

              <div
                v-if="activeTab === 1"
                class="w-full flex-1 flex justify-center flex-col gap-[14px]"
              >
                <div class="row">
                  <div class="col !pl-0">
                    <div class="title">
                      {{ $t("popup.automation.title_automation") }}
                    </div>
                  </div>
                </div>
                <div
                  class="row !flex-row flex justify-center gap-2 items-center"
                >
                  <div class="col bg-greyscale-100 dark:bg-[#282C34] flex">
                    <span>{{ $t("popup.automation.subtitle_server") }}</span>
                    <p>
                      {{ serverUrl }}
                    </p>
                  </div>
                  <div class="action-item">
                    <CopyButton :value="serverUrl" />
                  </div>
                </div>
                <div class="row">
                  <div class="col !pl-0">
                    <div class="title">
                      {{ $t("popup.automation.title_capabilities") }}
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="!flex-row flex justify-center gap-2 items-center">
                    <div class="col bg-greyscale-100 dark:bg-[#282C34]">
                      <span>{{ $t("popup.automation.subtitle_gw") }}</span>
                      <p>
                        {{ currentDevice.deviceCredential.momentumGw }}
                      </p>
                    </div>
                    <div class="action-item">
                      <CopyButton
                        :value="currentDevice.deviceCredential.momentumGw"
                      />
                    </div>
                  </div>
                  <div class="!flex-row flex justify-center gap-2 items-center">
                    <div class="col">
                      <span class="!flex-[0.35]">
                        {{ $t("popup.automation.subtitle_token") }}
                      </span>
                      <p>
                        {{ visibleToken }}
                      </p>
                      <Button
                        @click="toggleVisibility"
                        class="toggleVisibility-button default line-button link-default"
                      >
                        {{
                          showToken
                            ? $t("button.general.hide")
                            : $t("button.general.show")
                        }}
                      </Button>
                    </div>
                    <div class="action-item flex justify-end">
                      <CopyButton
                        :value="currentDevice.deviceCredential.momentumToken"
                      />
                    </div>
                  </div>

                  <div class="!flex-row flex justify-center gap-2 items-center">
                    <div class="col bg-greyscale-100 dark:bg-[#282C34]">
                      <span>{{ $t("popup.automation.subtitle_user") }}</span>
                      <p>
                        {{ currentDevice.deviceCredential.momentumUser }}
                      </p>
                    </div>
                    <div class="action-item flex justify-end">
                      <CopyButton
                        :value="currentDevice.deviceCredential.momentumUser"
                      />
                    </div>
                  </div>
                </div>

                <div class="row">
                  <div class="col !pl-0">
                    <div class="title">
                      {{ $t("popup.automation.title_appium") }}
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div
                    v-if="currentDevice.deviceOs.toLowerCase() === 'ios'"
                    class="!flex-row flex justify-center gap-2 items-center"
                  >
                    <div class="col bg-greyscale-100 dark:bg-[#282C34]">
                      <span>{{ $t("popup.automation.subtitle_proxy") }}</span>
                      <p>
                        {{ currentDevice.deviceCredential.momentumGw + 2000 }}
                      </p>
                    </div>
                    <div class="action-item">
                      <CopyButton
                        :value="
                          currentDevice.deviceCredential.momentumGw + 2000
                        "
                      />
                    </div>
                  </div>

                  <div
                    v-if="currentDevice.deviceOs.toLowerCase() === 'android'"
                    class="!flex-row flex justify-center gap-2 items-center"
                  >
                    <div class="col bg-greyscale-100 dark:bg-[#282C34]">
                      <span>{{ $t("popup.automation.subtitle_port") }}</span>
                      <p>
                        {{ currentDevice.deviceCredential.momentumGw + 2000 }}
                      </p>
                    </div>
                    <div class="action-item">
                      <CopyButton
                        :value="
                          currentDevice.deviceCredential.momentumGw + 2000
                        "
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="popup-bottom">
          <div>
            <a
              v-if="activeTab === 1"
              class="text-primary-base dark:text-greyscale-500 text-xs flex flex-row items-center gap-1 stroke-current"
              target="_blank"
              href="https://momentumsuite.com/docs/appium-capabilities/"
              >{{ $t("button.devicelist.learn") }}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M13 10.9998L21.2 2.7998"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M22.0002 6.8V2H17.2002"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M11 2H9C4 2 2 4 2 9V15C2 20 4 22 9 22H15C20 22 22 20 22 15V13"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </a>
          </div>
          <div>
            <Button
              :disabled="currentDevice.status !== 1"
              @click="goToDevice"
              :class="[
                currentStatus(currentDevice.status),
                {
                  launchButtonDisable:
                    isTrialUser && !currentDevice.isTrialDevice,
                },
              ]"
              class="h-10 w-[130px] rounded-full text-xs font-bold"
            >
              <div class="flex gap-2 items-center">
                <svg
                  v-if="isTrialUser && !currentDevice.isTrialDevice"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M16.7 18.98H7.29995C6.87995 18.98 6.40995 18.65 6.26995 18.25L2.12995 6.66999C1.53996 5.00999 2.22996 4.49999 3.64996 5.51999L7.54995 8.30999C8.19995 8.75999 8.93995 8.52999 9.21995 7.79999L10.98 3.10999C11.54 1.60999 12.47 1.60999 13.03 3.10999L14.79 7.79999C15.07 8.52999 15.81 8.75999 16.45 8.30999L20.11 5.69999C21.67 4.57999 22.42 5.14999 21.78 6.95999L17.74 18.27C17.59 18.65 17.12 18.98 16.7 18.98Z"
                    stroke="#fff"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    opacity="0.34"
                    d="M6.5 22H17.5"
                    stroke="#fff"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    opacity="0.34"
                    d="M9.5 14H14.5"
                    stroke="#fff"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <span>{{ launchButtonText }}</span>
              </div>
            </Button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import Button from "@/components/Button";
import CopyButton from "@/components/CopyButton";
import mixin from "@/mixins";
export default {
  name: "DevicePopup",
  components: { Button, CopyButton },
  mixins: [mixin],
  data() {
    return {
      tabs: [
        { id: 0, name: "popup.specs.tab" },
        { id: 1, name: "popup.automation.tab" },
      ],
      activeTab: 0,
      serverUrl: process.env.VUE_APP_APPIUM_SERVER,
      showToken: false,
    };
  },
  props: {
    currentDevice: Object,
    isOpen: Boolean,
  },
  computed: {
    ...mapGetters(["isTrialUser"]),
    getImageUrl() {
      return require(`@/assets/images/devices/${this.currentDevice.iconTag}`);
    },
    launchButtonText() {
      if (this.isTrialUser && !this.currentDevice.isTrialDevice) {
        return "Pro";
      }
      return this.$t(this.mapStatus(this.currentDevice.status)) ===
        this.$t("filter.status.available")
        ? this.$t("button.devicelist.launch")
        : this.$t(this.mapStatus(this.currentDevice.status));
    },
    isEnabled() {
      return this.currentDevice.status === 1;
    },
    visibleToken() {
      const firstFiveChars =
        this.currentDevice.deviceCredential.momentumToken.substring(0, 5);
      const hiddenChars = "*".repeat(
        this.currentDevice.deviceCredential.momentumToken.length - 5
      );
      return this.showToken
        ? this.currentDevice.deviceCredential.momentumToken
        : firstFiveChars + hiddenChars;
    },
  },
  methods: {
    ...mapActions(["emitMixPanelEvent"]),
    close() {
      this.$emit("close");
      this.activeTab = 0;
    },
    changeTab(id) {
      this.activeTab = id;
    },
    goToDevice() {
      if (
        (!this.isTrialUser || this.currentDevice.isTrialDevice) &&
        this.isEnabled
      ) {
        this.emitMixPanelEvent({
          event: "deviceLaunch",
          data: {
            launchDeviceOS: this.currentDevice.deviceOs,
            launchDeviceBrand: this.currentDevice.deviceBrand,
            launchDeviceModel: this.currentDevice.deviceModel,
            launchDeviceUDID: this.currentDevice.deviceToken,
            launchDeviceResolution: this.currentDevice.resolution,
            launchDeviceLocation: "InfoBox",
          },
        });
        this.$router.push({
          name:
            this.currentDevice.deviceOs === "android"
              ? "DeviceLauncherAndroid"
              : "DeviceLauncherIos",
          params: { deviceId: this.currentDevice.id },
        });
      }
    },
    toggleVisibility() {
      this.showToken = !this.showToken;
    },
  },
};
</script>
<style lang="css">
@import "index.module.css";

.modal-enter-active {
  animation: enter 0.3s ease-out;
}

@keyframes enter {
  0% {
    opacity: 0%;
    transform: scale(0);
  }
  100% {
    opacity: 100%;
    transform: scale(1);
  }
}
</style>
